/**
 * Client-side JWT inspection (no signature verification).
 * Aligns with beatly-discovery-app token rules for admin vs user sessions.
 */

export type BeatlyJwtTokenType = 'user' | 'admin' | 'anonymous' | 'invalid';

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Classify Beatly JWT by `user_id` shape (same as discovery `getTokenType`).
 * - user: ends with '-usr'
 * - admin: starts with 'admin-'
 * - anonymous: ends with '-ano'
 */
export function getJwtTokenType(token: string): BeatlyJwtTokenType {
  const payload = decodeJwtPayload(token);
  const userId = payload?.user_id;
  if (typeof userId !== 'string') return 'invalid';

  if (userId.endsWith('-usr')) return 'user';
  if (userId.startsWith('admin-')) return 'admin';
  if (userId.endsWith('-ano')) return 'anonymous';

  return 'invalid';
}

/** True when the stored JWT is a Beatly admin token (internal tooling). */
export function isAdminJwtToken(token: string | undefined | null): boolean {
  if (!token) return false;
  return getJwtTokenType(token) === 'admin';
}
